import moment from 'moment'
import { SUBMIT_REVIEW_ENDPOINT } from '../constant';

export const parseCronExpression = (cron) => {
  // Match the cron expression and extract its fields
  const match = cron.match(/^cron\((.*?)\)$/);
  if (!match) {
    console.log("Invalid cron expression format. Ensure it starts with 'cron(' and ends with ')'.");
    return null;
  }

  // Extract and split the cron fields
  const cronFields = match[1].trim();
  const fields = cronFields.split(/\s+/);

  // UNIX cron format
  if (fields.length !== 5) {
    console.log(`Invalid cron expression format. Expected 5 fields, but got ${fields.length}.`);
    return null;
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  
  // Map for day numbers to names
  const daysMap = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
  };

  // Convert 24-hour time to 12-hour format with AM/PM
  const hour24 = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);
  const isPM = hour24 >= 12;
  const hour12 = hour24 % 12 || 12; // Convert 0 to 12 for midnight
  const time12Hr = `${hour12}:${minute.padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
  const tempDate = new Date(`1970-01-01 ${time12Hr}`)

  // Parse dayOfWeek field
  const days = dayOfWeek.split(",").map((day) => {
    const trimmedDay = day.trim();
    if (!daysMap[trimmedDay]) {
      throw new Error(`Invalid day of the week: '${trimmedDay}'.`);
    }
    return daysMap[trimmedDay];
  });

  // Return parsed result
  return {
    time: time12Hr,
    days,
    time_sort: hour24 * 60 + minuteNum,
  };
}

export const daysOfWeek = [
  { id: 'Monday', name: 'Monday' },
  { id: 'Tuesday', name: 'Tuesday' },
  { id: 'Wednesday', name: 'Wednesday' },
  { id: 'Thursday', name: 'Thursday' },
  { id: 'Friday', name: 'Friday' },
  { id: 'Saturday', name: 'Saturday' },
  { id: 'Sunday', name: 'Sunday' }
];

export const convertTo24HourFormat = (time) => {
  const [timePart, period] = time.split(' ');  // Split time and AM/PM
  let [hours, minutes] = timePart.split(':'); // Split time into hours and minutes
  
  hours = Number(hours);
  minutes = Number(minutes);

  // Convert to 24-hour format
  if (period === 'PM' && hours < 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0; // Convert 12 AM to 00
  }

  // Ensure two digits for hours and minutes
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
}

export const convertToISO = (time) => {
  // Split the time and AM/PM
  const [timePart, period] = time.split(' ');
  let [hours, minutes] = timePart.split(':');
  
  hours = Number(hours);
  minutes = Number(minutes);

  // Convert to 24-hour format
  if (period === 'PM' && hours < 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0; // Convert 12 AM to 00
  }

  // Create a new date object and set the hours and minutes
  const date = new Date();
  date.setHours(hours, minutes, 0, 0); // Set hours and minutes, seconds and milliseconds to 0

  // Convert to ISO string
  return date.toISOString(); // Returns the ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
}

export const generateCronExpression = (days, utcTimeISO) => {
  // Step 1: Convert UTC time to local time
  const utcDate = new Date(utcTimeISO);
  const localDate = new Date(
    utcDate.toLocaleString("en-US", { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
  );
  const localHours = localDate.getHours(); // Local hours
  const localMinutes = localDate.getMinutes(); // Local minutes
  // Step 2: Map days to AWS cron-compatible values
  const dayMapping = {
    Sunday: 7,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  const cronDays = days.map((day) => dayMapping[day]).join(",");

  return `cron(${String(localMinutes).padStart(2, '0')} ${localHours} ? * ${cronDays})`;
}

export const isValidDoubleJSON = (str) => {
  try {
    const firstParse = JSON.parse(str); // First parse
    JSON.parse(firstParse); // Second parse to check double JSON
    return true;
  } catch (e) {
    return false;
  }
}

export const convertDateToCronDay = (selectedDate) => {
  const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  return dayOfWeek;
};

export const convertToAWSUTC = (localDateStr) => {
  // Manually extract year, month, and day
  const [year, month, day] = localDateStr.split("-").map(Number);

  // Start of the day (00:00 AM)
  const startOfDay = new Date(year, month - 1, day, 0, 0, 0);
  // End of the day (11:59 PM)
  const endOfDay = new Date(year, month - 1, day, 23, 59, 59);

  return {
    startUTC: startOfDay.toISOString(),
    endUTC: endOfDay.toISOString(),
  };
};

export const getVideogatewayURL = (url) => {
  const index = url.indexOf("videogateway");
  return index !== -1 ? url.substring(0, url.indexOf("/", index)) : "";
};

export const convertAWSDateToLocalTime = (awsDate) => {
  // Convert the AWS timestamp to local time and format it
  const localTime = moment(awsDate).local().format('dddd, MMM D, YYYY, h:mm:ss A');
  return localTime;
};

export const formatVideoGatewayURL = (url, cameraAssetId) => {
  let parsedUrl = url;
  parsedUrl = parsedUrl.replace("https://", "");
  parsedUrl = parsedUrl.replace("-rec.", "-cam.");

  if (parsedUrl.includes("recording")) {
    parsedUrl = parsedUrl.replace("recording", "api");
    parsedUrl = `${parsedUrl}/frame.jpeg?src=${cameraAssetId}`;
  } else if (parsedUrl.includes("stream.html")) {
    parsedUrl = parsedUrl.replace("stream.html", "api/frame.jpeg");
  }

  return `https://vg-cors.isems-devops.workers.dev/proxy/${parsedUrl}`;
};

export const convertImageToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
};

export const submitReportData = async (reportData) => {
  try {
    const response = await fetch(
      SUBMIT_REVIEW_ENDPOINT,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reportData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    console.log("API Response:", data);
  } catch (error) {
    console.error("API Error:", error);
  }
}